// Script de criação do usuário de backup por motor: o mínimo que o agente
// precisa, tirado do que cada driver executa (ADR 0023, ADR 0025, ADR 0031).
// A senha nunca entra no texto: fica como <senha>, para a pessoa trocar no
// cliente do banco.

export type GrantEngine = "postgresql" | "mysql" | "oracle" | "sqlserver";

export type GrantOptions = {
  user: string;
  /** Servidor informado no cadastro; vazio é o banco desta máquina. */
  host: string;
  /** Oracle: Data Pump ou RMAN. */
  mode?: "datapump" | "rman";
  /** SQL Server: bancos escolhidos; vazio com all é todos os bancos. */
  databases?: string[];
  all?: boolean;
  /** SQL Server no Windows: autenticação integrada com a conta do serviço do agente. */
  integrated?: boolean;
};

export const PASSWORD_PLACEHOLDER = "<senha>";

/** Conta do serviço do agente no Windows, que entra pela autenticação integrada. */
export const SQLSERVER_SYSTEM_LOGIN = "NT AUTHORITY\\SYSTEM";

function local(host: string): boolean {
  const h = host.trim().toLowerCase().replace(/^\[|\]$/g, "");
  return h === "" || h === "localhost" || h === "127.0.0.1" || h === "::1";
}

/** Script SQL para o motor, ou null quando o motor não usa usuário e senha. */
export function grantScript(engine: string, o: GrantOptions): string | null {
  const user = o.user.trim() || "ebackup";
  switch (engine) {
    case "postgresql":
      // pg_dump por banco e pg_dumpall --globals-only: leitura de tudo (PostgreSQL 14+).
      return [
        `CREATE ROLE "${user}" LOGIN PASSWORD '${PASSWORD_PLACEHOLDER}';`,
        `GRANT pg_read_all_data TO "${user}";`,
      ].join("\n");
    case "mysql": {
      // mysqldump --single-transaction --routines --triggers --events; RELOAD para o
      // FLUSH TABLES que o mysqldump 8 faz com GTID ligado.
      const from = local(o.host) ? "localhost" : "<endereço desta máquina>";
      const account = `'${user}'@'${from}'`;
      return [
        `CREATE USER ${account} IDENTIFIED BY '${PASSWORD_PLACEHOLDER}';`,
        `GRANT SELECT, SHOW VIEW, TRIGGER, EVENT, LOCK TABLES, RELOAD, REPLICATION CLIENT ON *.* TO ${account};`,
      ].join("\n");
    }
    case "oracle": {
      // Usuário comum (C##) em CDB: os comandos levam CONTAINER=ALL.
      const common = /^c##/i.test(user) ? " CONTAINER=ALL" : "";
      const privileges = o.mode === "rman"
        ? "CREATE SESSION, SELECT_CATALOG_ROLE, SYSBACKUP"
        : "CREATE SESSION, CREATE ANY DIRECTORY, SELECT_CATALOG_ROLE, DATAPUMP_EXP_FULL_DATABASE";
      return [
        `CREATE USER ${user} IDENTIFIED BY "${PASSWORD_PLACEHOLDER}"${common};`,
        `GRANT ${privileges} TO ${user}${common};`,
      ].join("\n");
    }
    case "sqlserver": {
      // BACKUP DATABASE ... WITH COPY_ONLY exige db_backupoperator em cada banco;
      // VIEW ANY DEFINITION deixa o agente ler o tamanho dos arquivos (sys.master_files).
      const login = o.integrated ? SQLSERVER_SYSTEM_LOGIN : user;
      const create = o.integrated
        ? `IF SUSER_ID(N'${login}') IS NULL CREATE LOGIN [${login}] FROM WINDOWS;`
        : `CREATE LOGIN [${login}] WITH PASSWORD = N'${PASSWORD_PLACEHOLDER}', CHECK_POLICY = ON;`;
      const perDatabase = (db: string) =>
        `USE [${db}]; CREATE USER [${login}] FOR LOGIN [${login}]; ALTER ROLE db_backupoperator ADD MEMBER [${login}];`;
      const names = (o.databases ?? []).map((d) => d.trim()).filter(Boolean);
      const grants = !o.all && names.length > 0
        ? names.map(perDatabase)
        : [`EXEC sp_MSforeachdb N'IF DB_ID(''?'') > 4 BEGIN USE [?]; CREATE USER [${login}] FOR LOGIN [${login}]; ALTER ROLE db_backupoperator ADD MEMBER [${login}]; END';`];
      return [create, `GRANT VIEW ANY DEFINITION TO [${login}];`, ...grants].join("\n");
    }
    default:
      return null;
  }
}
